/**
 * Shared layout for card tiles in search, collection, deck browser, and print tray.
 * Keep aspect ratio and frame radii in sync with `CardImage` to avoid corner mismatch.
 */

/** Standard portrait tile (poker-style), used by CardImage and grid skeletons */
export const CARD_GRID_ASPECT_RATIO = '5 / 7' as const;

/** Wide slot for battlefield-style art (deck panel landscape / print-aligned) */
export const CARD_GRID_ASPECT_RATIO_LANDSCAPE = '7 / 5' as const;

/**
 * Landscape width / portrait width so a 7∶5 battlefield matches a 5∶7 tile
 * at the same physical scale (shared short edge).
 */
export const BATTLEFIELD_TO_PORTRAIT_WIDTH = 7 / 5;

/**
 * Rune domain tiles are half a portrait column. Two domains sit in a row
 * whenever leftover identity space is at least one main-deck track.
 */
export const RUNE_TO_PORTRAIT_WIDTH = 0.5;

/**
 * Browse / collection / set galleries — floor for Auto before the grid is
 * measured. Once the pane width is known, {@link resolveGalleryColumns} packs
 * tiles of about {@link CARD_GRID_GALLERY_MIN_TILE_PX}, including on a narrow
 * tablet pane. The viewport ladder is only the fallback before that measurement.
 * Deck-builder Auto packs from pane width instead (see
 * {@link autoCardGridColumnCount}).
 */
export const CARD_GRID_COLUMNS_RESPONSIVE = {
  base: 2,
  sm: 3,
  md: 4,
  lg: 5,
  xl: 6,
} as const;

/**
 * Browse Auto adds a column once another tile of this width fits.
 * Keeps search/collection art near the size it has on a laptop, instead of
 * stretching six cards across a 24" monitor.
 */
export const CARD_GRID_GALLERY_MIN_TILE_PX = 180;

/** Default `SimpleGrid` gap — search / collection / deck card browser */
export const CARD_GRID_GAP = 4;

/** `gap={4}` → 1rem at a 16px root. Used when packing Auto columns from a measured width. */
export const CARD_GRID_GAP_PX = 16;

const GALLERY_MIN_COLUMNS = 2;
const GALLERY_MAX_COLUMNS = 10;

/** How many browse tiles of {@link CARD_GRID_GALLERY_MIN_TILE_PX} fit in `contentWidthPx`. */
export function galleryColumnCount(contentWidthPx: number): number {
  if (!Number.isFinite(contentWidthPx) || contentWidthPx <= 0) return 4;
  const packed = Math.floor(
    (contentWidthPx + CARD_GRID_GAP_PX) / (CARD_GRID_GALLERY_MIN_TILE_PX + CARD_GRID_GAP_PX),
  );
  return Math.max(GALLERY_MIN_COLUMNS, Math.min(GALLERY_MAX_COLUMNS, packed));
}

/**
 * Gallery Auto. A measured pane picks how many {@link CARD_GRID_GALLERY_MIN_TILE_PX}
 * tiles fit (2–10). The viewport ladder is used only before that width exists,
 * so a sidebar cannot force four columns into a narrow content pane.
 */
export function resolveGalleryColumns(
  contentWidthPx: number | null,
  breakpointColumns: number,
): number {
  if (contentWidthPx == null || !Number.isFinite(contentWidthPx) || contentWidthPx <= 0) {
    return breakpointColumns;
  }
  return galleryColumnCount(contentWidthPx);
}

/**
 * Minimum 1fr track for Auto packing. Same floor as deck-list thumbs so a
 * resizable builder pane fills with cards instead of two oversized tiles.
 */
export const CARD_GRID_AUTO_MIN_TILE_PX = 94;

const AUTO_MIN_COLUMNS = 2;
const AUTO_MAX_COLUMNS = 10;

/** Pack as many min-width tiles as fit in `contentWidthPx`, including gaps. */
export function autoCardGridColumnCount(contentWidthPx: number): number {
  if (!Number.isFinite(contentWidthPx) || contentWidthPx <= 0) return 4;
  const packed = Math.floor(
    (contentWidthPx + CARD_GRID_GAP_PX) / (CARD_GRID_AUTO_MIN_TILE_PX + CARD_GRID_GAP_PX),
  );
  return Math.max(AUTO_MIN_COLUMNS, Math.min(AUTO_MAX_COLUMNS, packed));
}

/** Print page grid: slightly tighter on small screens — use with `useBreakpointValue` */
export const CARD_GRID_GAP_PRINT = {
  base: 2,
  md: 4,
} as const;

/**
 * Standard Riftbound card corners scale with the card art, so use a percentage
 * radius instead of Chakra's fixed `md`/`lg` tokens.
 */
export const CARD_ART_RADIUS_ROUNDED = {
  frame: '6% / 4%',
  image: '6% / 4%',
} as const;

/** When CardImage `rounded={false}` */
export const CARD_ART_RADIUS_UNROUNDED = {
  frame: 'md',
  image: 'sm',
} as const;

export const CARD_GRID_INFINITE_SCROLL_ROOT_MARGIN = '300px';

/**
 * Max rendered tile width (px) for hover art peek on browse + deck-panel grids.
 * Above this, the in-grid art is large enough that a peek adds little.
 */
export const CARD_GRID_ART_PEEK_MAX_TILE_PX = 220;

/** Skeleton tile count for initial loading states */
export function cardGridSkeletonTileCount(
  columns: number | undefined,
): number {
  if (typeof columns === 'number' && Number.isFinite(columns) && columns > 0) {
    return Math.min(16, columns * 2);
  }
  return 12;
}
